import { useSyncExternalStore } from "react";

/** Toasts live outside React's tree: a toast said just before the page moves (a workspace renamed to a new address remounts
 *  everything under it) still shows on the page that follows. A toast may carry one action (Undo), which is gone with it.
 *  The first setup round (2026-10-22, DECISIONS "The first setup, round one"): Omnalingo's shape, so each toast says what kind of news it
 *  is (`success`, `error`, `warning`, `info`; no kind is info) and may carry a second line (`detail`). */
export type ToastAction = { label: string; onClick: () => void };
export type ToastKind = "success" | "error" | "warning" | "info";
export type ToastOptions = { kind?: ToastKind; detail?: string; action?: ToastAction };
export type Toast = { id: number; text: string; kind: ToastKind; detail?: string; action?: ToastAction };
let toasts: Toast[] = []; let nextId = 0;
const subscribers = new Set<() => void>();
const emit = () => subscribers.forEach((s) => s());
export const UNDO_MS = 5000;
/** How long a toast stays (the ticket page and marks round, 2026-10-23): 5 s, an error or a second line 8 s, to be read; an action keeps
 *  the Undo window exactly. */
const PLAIN_MS = 5000, LONG_MS = 8000;
const lifetime = (o: ToastOptions) => (o.action ? UNDO_MS : o.kind === "error" || o.detail ? LONG_MS : PLAIN_MS);
/** Each toast's clock: its timer while it runs, the time it still has while the pointer holds the toasts (the clock is paused, and a
 *  release starts it again from there). */
type Clock = { timer: ReturnType<typeof setTimeout> | null; endsAt: number; left: number };
const clocks = new Map<number, Clock>();
let held = false;
function run(id: number, ms: number) { clocks.set(id, { timer: setTimeout(() => dismiss(id), ms), endsAt: Date.now() + ms, left: ms }); }
export function toast(text: string, o: ToastOptions = {}, ms = lifetime(o)): Toast {
  const t: Toast = { id: ++nextId, text, kind: o.kind ?? "info", ...(o.detail ? { detail: o.detail } : {}), ...(o.action ? { action: o.action } : {}) };
  toasts = [...toasts, t]; emit();
  if (held) clocks.set(t.id, { timer: null, endsAt: 0, left: ms }); else run(t.id, ms);
  return t;
}
export function dismiss(id: number) { const c = clocks.get(id); if (c?.timer) clearTimeout(c.timer); clocks.delete(id); toasts = toasts.filter((x) => x.id !== id); emit(); }
/** The pointer is on the toasts (or a tap opened the stack): every clock pauses; released, each toast gets the time it still had. */
export function holdToasts(on: boolean) {
  if (on === held) return;
  held = on;
  for (const [id, c] of clocks) {
    if (on) { if (c.timer) clearTimeout(c.timer); clocks.set(id, { timer: null, endsAt: 0, left: Math.max(0, c.endsAt - Date.now()) }); }
    else run(id, c.left);
  }
}
const subscribe = (s: () => void) => { subscribers.add(s); return () => { subscribers.delete(s); }; };
const snapshot = () => toasts;
export const useToasts = () => useSyncExternalStore(subscribe, snapshot, snapshot);
